export const WAT_TIME_ZONE = 'Africa/Lagos';

export const REVIEWED_STATUSES = new Set(['Approved', 'Flagged', 'Rejected']);
export const CLOSED_VISIT_STATUSES = new Set(['Submitted', 'Approved', 'Flagged', 'Rejected', 'Cancelled']);

export const toDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const getVisitStart = (visit) => (
  toDate(visit?.scheduledStart || visit?.scheduledDate || visit?.startTime || visit?.actualDate)
);

export const getVisitEnd = (visit) => (
  toDate(visit?.scheduledEnd || visit?.endTime)
);

export const formatWatDate = (value, options = {}) => {
  const date = toDate(value);
  if (!date) return 'Date not set';
  return new Intl.DateTimeFormat('en-NG', {
    timeZone: WAT_TIME_ZONE,
    weekday: options.weekday || 'short',
    month: 'short',
    day: 'numeric',
    year: options.year || undefined,
  }).format(date);
};

export const formatWatTime = (value) => {
  const date = toDate(value);
  if (!date) return 'Time not set';
  return `${new Intl.DateTimeFormat('en-NG', {
    timeZone: WAT_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date)} WAT`;
};

export const formatWatDateTime = (value) => {
  const date = toDate(value);
  if (!date) return 'Time not set';
  return `${formatWatDate(date, { year: 'numeric' })} at ${formatWatTime(date)}`;
};

export const hasGpsCheckIn = (visit) => Boolean(
  visit?.verification?.gps?.checkInLocation ||
  visit?.verification?.gps?.check_in_location ||
  visit?.visitSession?.checkedIn ||
  visit?.visit_session?.checked_in ||
  visit?.startTime
);

export const normalizeVisitStatus = (status) => (
  status === 'Under Review' ? 'Submitted' : status
);

export const getVisitActionLabel = (visit) => {
  const status = normalizeVisitStatus(visit?.status);
  if (status === 'Scheduled') return hasGpsCheckIn(visit) ? 'Continue Visit' : 'Start Visit';
  if (status === 'Submitted') return 'Awaiting Review';
  if (status === 'Approved') return 'View Review';
  if (status === 'Flagged') return 'View Review';
  if (status === 'Rejected') return 'View Review';
  return status || 'View Visit';
};

export const getStatusClasses = (status) => {
  switch (normalizeVisitStatus(status)) {
    case 'Scheduled':
      return 'bg-[#F5E8EA] text-[#9F1D35] border-[#E3C9CE]';
    case 'Submitted':
      return 'bg-amber-50 text-amber-800 border-amber-200';
    case 'Approved':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'Flagged':
      return 'bg-red-50 text-red-700 border-red-200';
    case 'Rejected':
      return 'bg-neutral-100 text-neutral-700 border-neutral-200';
    case 'Cancelled':
      return 'bg-neutral-100 text-neutral-500 border-neutral-200';
    default:
      return 'bg-[#FAFAF7] text-[#5A5A5A] border-[#E8E6E3]';
  }
};

export const getDependantName = (dependant, fallback = 'Dependant') => {
  if (!dependant) return fallback;
  return dependant.name || [dependant.firstName, dependant.lastName].filter(Boolean).join(' ') || fallback;
};

export const getVisitDependantName = (visit, dependant) => (
  visit?.dependantSnapshot?.name ||
  visit?.dependantName ||
  getDependantName(dependant, 'Dependant')
);

export const getVisitAddress = (visit, dependant) => {
  const snapshot = visit?.dependantSnapshot;
  if (snapshot?.addressText) return snapshot.addressText;
  const address = snapshot?.address || dependant?.address;
  if (!address) return 'Address not available';
  return [
    address.street,
    address.neighbourhood,
    address.landmark,
    address.lga || address.city,
    address.state,
    address.country,
  ].filter(Boolean).join(', ') || 'Address not available';
};

export const getVisitPhone = (visit, dependant) => (
  visit?.dependantSnapshot?.phone || dependant?.phone || 'Phone not available'
);

export const getChecklistValue = (visit, path, fallback = '-') => {
  let current = visit?.checklist;
  for (const key of path) {
    if (!current || typeof current !== 'object') return fallback;
    const snake = key.replace(/[A-Z]/g, (char) => `_${char.toLowerCase()}`);
    current = current[key] ?? current[snake];
  }
  return current ?? fallback;
};

export const getLatestReview = (visit) => (
  visit?.latestReview || visit?.supervisorReview || null
);

export const getReviewHistory = (visit) => (
  Array.isArray(visit?.reviewHistory) ? visit.reviewHistory : []
);

export const sortVisitsNewestFirst = (items) => [...items].sort((a, b) => {
  const aDate = getVisitStart(a)?.getTime() || 0;
  const bDate = getVisitStart(b)?.getTime() || 0;
  return bDate - aDate;
});

export const sortVisitsOldestFirst = (items) => [...items].sort((a, b) => {
  const aDate = getVisitStart(a)?.getTime() || 0;
  const bDate = getVisitStart(b)?.getTime() || 0;
  return aDate - bDate;
});
